import {
  Component,
  ElementRef,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { ZXingScannerComponent } from '@zxing/ngx-scanner';
import { BarcodeFormat, Result } from '@zxing/library';
import { UtilService } from '../../core/services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { THIS_EXPR } from '@angular/compiler/src/output/output_ast';

@Component({
  selector: 'app-repair-scan-device',
  templateUrl: './repair-scan-device.component.html',
  styleUrls: ['./repair-scan-device.component.scss'],
})
export class RepairScanDeviceComponent implements OnInit, OnDestroy {
  @ViewChild('scanner', { read: false })
  scanner: ZXingScannerComponent;
  @ViewChild('serialNoInput') serialNoInput: ElementRef;

  title;
  hasDevices = false;
  hasPermission: boolean;
  qrResult: Result;
  availableDevices: MediaDeviceInfo[] = [];
  currentDevice: MediaDeviceInfo = null;
  displayQRCodeError = false;
  displayQRCodeMsg = '';
  qrResultString: any;
  serialNo = '';
  isDisable = true;
  allowedFormats = [BarcodeFormat.CODE_128];
  selectedAction;
  pattern = /^[0-9]*$/;
  tryHarder = true;
  backCameraList = [];
  cameras;
  selected: MediaDeviceInfo = null;
  displayDevices: MediaDeviceInfo[] = [];
  autoStart;
  showScannerDiv = true;
  deviceSNLength = 24;
  parts = [];
  wrongBarcodeErrMsg = '';
  noBarcodeErrMsg = '';

  constructor(
    private utilService: UtilService,
    private router: Router,
    private zone: NgZone,
    public translate: TranslateService
  ) {
    // tslint:disable-next-line: no-string-literal
    window['angularComponentReference'] = {
      zone: this.zone,
      componentFn: (searchcontent: any) =>
        // tslint:disable-next-line: no-string-literal
        window['scannerOutput'](searchcontent),
      component: this,
    };

    this.translate
      .stream(['DASHBOARD.PARTS', 'WRONG_BARCODE', 'SCAN_BARCODE'])
      .subscribe((res) => {
        // console.log(res);
        this.parts = res['DASHBOARD.PARTS'];
        this.wrongBarcodeErrMsg = res['WRONG_BARCODE'];
        this.noBarcodeErrMsg = res['SCAN_BARCODE'];
        this.init();
      });
  }

  ngOnInit() {
    this.utilService.checkIfLoggedIn(); // check if user is logged in

    // to remove expressionchanged error
    // https://github.com/zxing-js/ngx-scanner/issues/270
    this.scanner.reset = function () {
      setTimeout(() => {
        this.deviceChange.emit(null);
      });
    };

    // this.init();

    // let devices = await navigator.mediaDevices.enumerateDevices();
    // let cameras = devices.filter((device) => device.kind === 'videoinput');
    // this.displayDevices = cameras.slice();
  }

  // In this method, we are trying to get the back cameras available on the mobile device on which this application is served. The library ngx-scanner by default gives us all the audio and video devices available via the cameraDevices parameter. But it is observed that in some mobile devices like Samsung, this doesn't work so we need to use the navigator.enumeratedevices API and get the devices.
  // From the devices available, we are filtering out the back-cameras only since the scanning will be carried out using the back-cameras. We are also trying to set the first camera as the default camera. This is done because in Samsung, by default the wide-lens camera is set as default and scanning doesnt work using the same!
  async onCamerasFound(cameraDevices: MediaDeviceInfo[]) {
    let cameraDeviceList: MediaDeviceInfo[] = [];

    // filter out video only
    this.cameras = cameraDevices.filter(
      (device) => device.kind === 'videoinput'
    );

    // filter out only back cameras
    this.cameras = this.cameras.filter((camera) =>
      this.utilService.isBackCameraLabel(camera.label)
    );

    // console.table(this.cameras);

    if (this.cameras.length > 0) {
      cameraDeviceList = this.cameras.slice();
    } else {
      let devices = await navigator.mediaDevices.enumerateDevices();
      let cameras = devices.filter((device) => device.kind === 'videoinput');
      cameras = cameras.filter((camera) =>
        this.utilService.isBackCameraLabel(camera.label)
      );
      cameraDeviceList = cameras.slice();
    }

    this.hasDevices = Boolean(cameraDeviceList && cameraDeviceList.length);

    this.availableDevices = cameraDeviceList.slice(); // this.cameras.slice();

    this.availableDevices = this.availableDevices.sort((camera1, camera2) =>
      camera1.label.localeCompare(camera2.label)
    );

    if (this.availableDevices && this.availableDevices.length > 0) {
      // fetch camera selection
      const currentDevice = this.utilService.getCameraSelection();

      // set first camera on load
      let persistCamera;
      if (currentDevice !== null && currentDevice !== undefined) {
        persistCamera = currentDevice.deviceId;
      } else {
        persistCamera = this.availableDevices[0].deviceId;
      }

      this.onDeviceSelectChange(persistCamera);
    }
  }

  onDeviceSelectChange(selected: string) {
    // console.log({ selected });

    this.currentDevice = null;

    const device = this.availableDevices.find((x) => x.deviceId === selected);

    this.currentDevice = device || null;

    this.selected = this.currentDevice;

    // console.log(this.currentDevice);

    // set camera selection in service
    this.utilService.setCameraSelection(this.selected);

    this.autoStart = true;

    if (this.currentDevice) {
      this.showScannerDiv = true;
    } else {
      this.showScannerDiv = false;
    }
  }

  init() {
    // we are setting the title of the page to the action selected in the dashboard page
    this.selectedAction = this.utilService.getAction();
    if (
      this.selectedAction &&
      this.selectedAction.hasOwnProperty('key') &&
      this.selectedAction['key'] !== null
    ) {
      // The property parts contains the array from the language file. We are getting the filtered value and assigning the text
      this.title = this.parts.filter(
        (obj) => obj.key.toLowerCase() === this.selectedAction.key.toLowerCase()
      )[0].value;
      // this.title = this.selectedAction.value;
    }
  }

  // scanning QR code
  handleQrCodeResult(resultString: string) {
    this.qrResultString = resultString;
    if (this.qrResultString !== null && this.qrResultString.trim() !== '') {
      if (
        this.qrResultString.length === this.deviceSNLength &&
        this.pattern.test(this.qrResultString)
      ) {
        this.serialNo = this.qrResultString;
        // the below line allows to scan multiple times
        this.serialNoInput.nativeElement.value = this.qrResultString;
        this.displayQRCodeError = false;
        this.displayQRCodeMsg = '';
        this.isDisable = false;
        this.scanner.reset();
      } else {
        this.displayQRCodeError = true;
        this.displayQRCodeMsg = this.wrongBarcodeErrMsg; //'Wrong barcode';
        this.isDisable = true;
      }
    } else {
      this.displayQRCodeError = true;
      this.displayQRCodeMsg = this.noBarcodeErrMsg; // 'Scan or enter barcode';
      this.isDisable = true;
    }

    // setTimeout(() => {
    //   this.next();
    // }, 500);
  }

  // manual entry of QR code
  handleManualQRCodeEntry(e) {
    this.handleQrCodeResult(e.target.value);
  }

  // on clicking next
  next() {
    this.utilService.setDevieSN(this.serialNo);

    this.scanner.reset();
    // localStorage.setItem('deviceSN', this.serialNo);
    this.router.navigate(['/scan-broken-serial']);
  }

  back() {
    this.router.navigate(['/dashboard']);
  }

  displayCameras(cameras: MediaDeviceInfo[]) {
    this.availableDevices = cameras;
  }

  openInfoCard() {
    this.utilService.openInfoCard();
  }

  ngOnDestroy(): void {
    this.scanner.enable = false;
  }
}
